import React from "react";
import { Field } from "../components/ui.js";
import type { Summary } from "./wizard.js";

/** A source's address as people read it: host and path, no scheme, no `www.`. */
function shortUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/$/, "");
    return `${parsed.hostname.replace(/^www\./, "")}${path}`;
  } catch {
    return url;
  }
}

/**
 * A summary somebody else wrote, put where the customer's own words would go.
 *
 * The same two fields as "Describe it myself", filled in and editable, with
 * the pages it came from underneath — so it is judged and corrected like a
 * draft, not accepted like a fact. Only what is in the fields reaches the film.
 */
export const AiSummary: React.FC<{
  state: { phase: "looking"; host: string } | { phase: "ready"; host: string; summary: Summary };
  onChange: (summary: Summary) => void;
}> = ({ state, onChange }) => {
  if (state.phase === "looking") {
    return (
      <p className="rounded-[10px] border border-[var(--edge)] bg-bay-900 p-4 text-[12.5px] text-ink-300">
        Looking up {state.host}… This takes about half a minute.
      </p>
    );
  }

  const { summary, host } = state;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[11.5px] font-medium text-ink-400">Written by AI from public pages about {host}</p>
      <Field label="Product name">
        <input
          className="input w-full"
          value={summary.name}
          maxLength={60}
          onChange={(event) => onChange({ ...summary, name: event.target.value })}
        />
      </Field>
      <Field label="What it does">
        <textarea
          className="input min-h-[96px] w-full resize-y"
          value={summary.summary}
          maxLength={4000}
          onChange={(event) => onChange({ ...summary, summary: event.target.value })}
        />
      </Field>
      {summary.sources.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {summary.sources.map((source) => (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              title={source.title}
              className="rounded-full border border-[var(--edge)] bg-bay-900 px-2 py-0.5 text-[11px] text-ink-300 hover:text-ink-100"
            >
              {shortUrl(source.url)}
            </a>
          ))}
        </div>
      ) : null}
      <p className="text-[11.5px] leading-[1.5] text-ink-400">
        Check it and change anything that's wrong. Only what's written here can end up in the film.
      </p>
    </div>
  );
};
